import React, { useEffect, useRef, useState } from 'react';
import { IS_NATIVE } from '../../utils/capacitor';

const ADSENSE_CLIENT = 'ca-pub-3882470562736719';

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

const AdSenseUnit = ({
  isPro = false,
  adSlot,
  adFormat = 'auto',
  fullWidthResponsive = true,
  adLayout,
  adLayoutKey,
  style = {},
  containerStyle = {},
  className = '',
  showLabel = true,
  lazy = true,
  minHeight = 60
}) => {
  const adsDisabled = isLocalDevHost();
  const [isVisible, setIsVisible] = useState(!lazy);
  const observerTargetRef = useRef(null);
  const insRef = useRef(null);

  useEffect(() => {
    if (adsDisabled || isPro || isVisible || !lazy) return;

    const target = observerTargetRef.current;
    if (!target) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [adsDisabled, isPro, isVisible, lazy]);

  useEffect(() => {
    if (adsDisabled || isPro || !isVisible || typeof window === 'undefined') return;

    const adElement = insRef.current;
    if (!adElement) return;

    if (adElement.getAttribute('data-adsbygoogle-status')) return;

    const timeoutId = window.setTimeout(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        console.warn('AdSense push error', error);
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [adsDisabled, isPro, isVisible]);

  // Do not make AdSense requests from localhost. `data-adtest` still contacts
  // Google and can return 403 for an unapproved local request.
  if (adsDisabled || isPro || !adSlot || IS_NATIVE) return null;

  const adFormatAttr = adFormat ? { 'data-ad-format': adFormat } : {};
  const adLayoutAttr = adLayout ? { 'data-ad-layout': adLayout } : {};
  const adLayoutKeyAttr = adLayoutKey ? { 'data-ad-layout-key': adLayoutKey } : {};

  return (
    <div className={`ad-unit ${className}`.trim()} style={containerStyle}>
      {showLabel && <p className="ad-unit-label">Advertisement</p>}
      <div
        ref={observerTargetRef}
        className="ad-unit-shell"
        style={{ minHeight }}
      >
        {isVisible && (
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={{ display: 'block', ...style }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={adSlot}
            data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
            {...adFormatAttr}
            {...adLayoutAttr}
            {...adLayoutKeyAttr}
          />
        )}
      </div>
    </div>
  );
};

export default AdSenseUnit;
